import * as z from "zod";
/**
 * Providers can be multiple types, and store information differently.
 * SAML providers are of type "saml" and the ID is the team tied to that SAML configuration
 * OIDC providers are of type "oauth" and the ID is the same as the OIDC provider for in [...nextauth].ts
 *
 * For both, the name does not affect authentication but the SAML providers use the name to
 * display the SAML provider's logo
 */
export interface Provider {
  id: string;
  name: string;
  type: string;
}

// Should match auth-providers LoginProvider
export const ProvidersSchema = z.array(
  z
    .object({
      id: z.string(),
      name: z.string(),
      type: z.string(),
      entityId: z.string().optional(), // disambiguating field for companies migrating saml/oidc on same platform
    })
    .passthrough(),
);

export const getCustomProviders = ({
  helmCustomAuthProviders,
  customAuthProviders,
}: {
  helmCustomAuthProviders: string | null;
  customAuthProviders: string | null;
}): Provider[] | null => {
  // Looks like '[{"type":"saml","id":"sce3dp9qa3mb","name":"Okta"}]'
  // id is the same as team in LoginSpec of frontend/utils/auth-providers
  // when putting these into doppel, be very careful not to insert whitespace into the string!
  const rawProvider = helmCustomAuthProviders || customAuthProviders;
  if (!rawProvider) {
    return null;
  }

  try {
    return ProvidersSchema.parse(JSON.parse(rawProvider));
  } catch (err) {
    console.error(
      "Failed to parse custom auth provider schema, falling back to Okta or default Google/Azure providers",
      rawProvider,
      err,
    );
    const providerIds = rawProvider.split(",");

    return providerIds.map((providerId) => {
      // Legacy case: this handles melio case currently
      return { id: providerId, name: "Okta", type: "oauth" };
    });
  }
};

export const DEFAULT_PROVIDERS = [
  { id: "google", name: "Google", type: "oauth" },
  { id: "azure-ad", name: "Azure", type: "oauth" },
];

export const GITHUB_PROVIDER = { id: "github", name: "GitHub", type: "oauth" };

export const getDefaultProviders = (showGithub: boolean): Provider[] =>
  showGithub ? [...DEFAULT_PROVIDERS, GITHUB_PROVIDER] : DEFAULT_PROVIDERS;

export const DEFAULT_GENERIC_ID_SET = new Set(["google", "azure-ad"]);
